/**
 * NameDialog — small modal with one text input (FR-015 /agent workspace).
 *
 * Create/rename surface for projects and conversations. Built on the
 * design-system ``Dialog`` (Radix portal primitive): backdrop scrim, Escape,
 * focus-trap and initial-focus all come for free — no manual keydown listener
 * or setTimeout-focus hack. Carries a single form input; submit on Enter.
 * Kept page-local — if a second page needs it, promote it to the design system.
 */
import React, { useEffect, useState } from 'react';
import {
  Button,
  Dialog,
  DialogContent,
  DialogFooter,
  DialogTitle,
  Input,
} from '@hofsync/design-system';

export interface NameDialogProps {
  isOpen: boolean;
  title: string;
  label: string;
  initialValue?: string;
  confirmLabel: string;
  cancelLabel: string;
  loading?: boolean;
  onSubmit: (name: string) => void;
  onCancel: () => void;
}

export const NameDialog: React.FC<NameDialogProps> = ({
  isOpen,
  title,
  label,
  initialValue = '',
  confirmLabel,
  cancelLabel,
  loading = false,
  onSubmit,
  onCancel,
}) => {
  const [value, setValue] = useState(initialValue);

  // Re-seed the draft each time the dialog opens for a (new) target.
  useEffect(() => {
    if (isOpen) setValue(initialValue);
  }, [isOpen, initialValue]);

  const trimmed = value.trim();
  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    if (!trimmed || loading) return;
    onSubmit(trimmed);
  };

  return (
    <Dialog
      open={isOpen}
      onOpenChange={(open) => {
        // Escape / backdrop-click / X → Radix drives close; route to onCancel.
        if (!open) onCancel();
      }}
    >
      <DialogContent
        data-testid="agent-name-dialog"
        // No description content; suppress Radix's missing-description warning.
        aria-describedby={undefined}
      >
        <DialogTitle>{title}</DialogTitle>
        <form onSubmit={handleSubmit}>
          <label
            style={{
              display: 'flex',
              flexDirection: 'column',
              gap: '0.375rem',
              fontSize: '0.8125rem',
            }}
          >
            {label}
            <Input
              value={value}
              onChange={(e) => setValue(e.target.value)}
              maxLength={200}
              data-testid="agent-name-dialog-input"
            />
          </label>
          <DialogFooter>
            <Button type="button" variant="ghost" onClick={onCancel} disabled={loading}>
              {cancelLabel}
            </Button>
            <Button type="submit" disabled={!trimmed || loading} data-testid="agent-name-dialog-confirm">
              {confirmLabel}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
};
